import { EighthWallRuntime } from "@/runtime/EighthWallRuntime";
import type { CapabilityReport, PlacementPose, RuntimePhase } from "@/runtime/types";
import { WebXRRuntime } from "@/runtime/WebXRRuntime";
import type { DemoId } from "@/demos/types";

export interface RuntimeFlowDependencies {
  activeDemoId: DemoId;
  demoTitle: string;
  requestId: number;
  isMounted: () => boolean;
  isCurrent: (requestId: number) => boolean;
  preloadDemo: (id: DemoId) => Promise<void>;
  getCapabilities: () => Promise<CapabilityReport>;
  webxrRuntime: WebXRRuntime;
  eighthWallRuntime: EighthWallRuntime;
  stopCamera: () => void;
  startCameraViewer: (recoveryMessage?: string) => Promise<void>;
  continueIn3D: (message?: string) => void;
  setStarted: (started: boolean) => void;
  setMode: (mode: CapabilityReport["preferredMode"]) => void;
  setPhase: (phase: RuntimePhase) => void;
  setError: (error: string | null) => void;
  setPlacement: (placement: PlacementPose | null) => void;
  setPlacementProgress: (progress: number) => void;
  setWebxrActive: (active: boolean) => void;
  showNotice: (message: string) => void;
  reportError: (cause: unknown, context: string) => void;
}

export async function startRuntimeFlow(deps: RuntimeFlowDependencies): Promise<void> {
  deps.setStarted(true);
  deps.setPhase("loading-engine");
  deps.setError(null);

  try {
    await deps.preloadDemo(deps.activeDemoId);
  } catch (cause) {
    if (!isActive(deps)) return;
    deps.reportError(cause, `${deps.demoTitle} asset load failed`);
    deps.setError("The experience asset could not be loaded. Check the connection and try again.");
    deps.setPhase("error");
    return;
  }

  if (!isActive(deps)) return;
  deps.setPhase("checking");

  try {
    const capability = await deps.getCapabilities();
    if (!isActive(deps)) return;

    if (capability.preferredMode === "webxr") {
      await startWebXr(deps);
      return;
    }

    if (capability.preferredMode === "eighth-wall") {
      await prepareEighthWall(deps);
      return;
    }

    if (capability.preferredMode === "camera-viewer") {
      await deps.startCameraViewer();
      return;
    }

    deps.continueIn3D();
  } catch (cause) {
    if (!isActive(deps)) return;
    console.warn("WebXR startup failed; trying the camera viewer.", cause);
    deps.setWebxrActive(false);
    await deps.webxrRuntime.stop("error");
    if (!isActive(deps)) return;
    await deps.startCameraViewer("AR unavailable · opening camera preview");
  }
}

async function startWebXr(deps: RuntimeFlowDependencies): Promise<void> {
  deps.setMode("webxr");
  deps.setWebxrActive(true);
  await deps.webxrRuntime.start();

  if (!isActive(deps)) {
    deps.setWebxrActive(false);
    await deps.webxrRuntime.stop("unmount");
    return;
  }

  const snapshot = deps.webxrRuntime.getSnapshot();
  if (snapshot.placementMode === "fixed") {
    deps.setPlacement(snapshot.placement);
    deps.setPlacementProgress(1);
    deps.setPhase("placed");
    deps.showNotice("AR ready · surface tracking unavailable");
    return;
  }

  deps.setPlacement(null);
  deps.setPlacementProgress(0);
  deps.setPhase("searching-surface");
}

async function prepareEighthWall(deps: RuntimeFlowDependencies): Promise<void> {
  deps.stopCamera();
  deps.setPhase("loading-engine");

  try {
    await deps.eighthWallRuntime.preload();
  } catch (cause) {
    deps.reportError(cause, "8th Wall preload failed");
    if (isActive(deps)) await deps.startCameraViewer();
    return;
  }

  if (!isActive(deps)) return;
  deps.setMode("eighth-wall");
  deps.setPlacement(null);
  deps.setPlacementProgress(0);
  deps.setPhase("requesting-permission");
}

function isActive(deps: RuntimeFlowDependencies): boolean {
  return deps.isMounted() && deps.isCurrent(deps.requestId);
}
